import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Color,
  Icon,
  Input,
  Select,
  Spin,
  Text,
} from '@bb/elements-web';
import { validateAddress } from 'api/SupraController';
import styled from 'styled-components';
import {
  CanManageBillingProfileType,
  useSubscriptionsPageContext,
} from '../../SubscriptionsPageContext';
import { CardStatus } from './CardStatus';

type BillingAddressCardProps = {
  /** Whether the user can manage billing details. */
  canManageBilling: CanManageBillingProfileType;
};

/**
 * The "Address" section within the Billing Contact Details card.
 * User/Brokerage can view & update their physical address from here.
 */
const BillingAddressCard: React.FC<BillingAddressCardProps> = ({
  canManageBilling,
}) => {
  const {
    billingProfile,
    updateBillingProfile,
    isFetchingProfile,
    isUpdatingProfile,
    hasSavedCard,
  } = useSubscriptionsPageContext();

  const [updatedAddress1, setUpdatedAddress1] = useState('');
  const [updatedAddress2, setUpdatedAddress2] = useState('');
  const [updatedCity, setUpdatedCity] = useState('');
  const [updatedState, setUpdatedState] = useState('');
  const [updatedZip, setUpdatedZip] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);
  const [updateProfileError, setUpdateProfileError] = useState<Error>();

  const formattedAddress = useMemo(() => {
    const address = [
      billingProfile?.Address1,
      billingProfile?.Address2,
      billingProfile?.City,
      billingProfile?.State,
      billingProfile?.Zip,
      billingProfile?.Country,
    ]
      .filter(str => str)
      .join(', ');
    return address;
  }, [billingProfile]);

  /** Whether or not they have filled in all the fields */
  const isCompleteAddress = useMemo(
    () =>
      updatedAddress1 &&
      updatedCity &&
      updatedState &&
      updatedZip &&
      selectedCountry,
    [selectedCountry, updatedAddress1, updatedCity, updatedState, updatedZip],
  );

  // Define some simple methods to handle actions in the form

  /** Switch the UI from displaying the address to editing the address */
  const switchToEditMode = useCallback(() => {
    if (canManageBilling.response) {
      setUpdatedAddress1(billingProfile?.Address1 || '');
      setUpdatedAddress2(billingProfile?.Address2 || '');
      setUpdatedCity(billingProfile?.City || '');
      setUpdatedState(billingProfile?.State || '');
      setUpdatedZip(billingProfile?.Zip || '');
      if (billingProfile?.Country) {
        setSelectedCountry(billingProfile?.Country || '');
      }
      setIsInEditMode(true);
    }
  }, [canManageBilling, billingProfile]);

  /** Switch the UI from editing the address to displaying the static address */
  const resetAndCloseEditMode = useCallback(() => {
    setUpdatedAddress1('');
    setUpdatedAddress2('');
    setUpdatedCity('');
    setUpdatedState('');
    setUpdatedZip('');
    setSelectedCountry('');
    setIsInEditMode(false);
  }, []);

  const updateAddress = useCallback(async () => {
    /** Whether this is a real location in the US or CA */
    async function isValidAddress() {
      if (!selectedCountry) return false;

      return validateAddress({
        city: updatedCity,
        stateCode: updatedState,
        zipCode: updatedZip,
        countryCode: selectedCountry,
      });
    }

    if (canManageBilling.response) {
      setIsValidatingAddress(true);
      if (isCompleteAddress && (await isValidAddress())) {
        const updatedProfile = { ...billingProfile };
        updatedProfile.Address1 = updatedAddress1;
        updatedProfile.Address2 = updatedAddress2;
        updatedProfile.City = updatedCity;
        updatedProfile.State = updatedState;
        updatedProfile.Zip = updatedZip;
        updatedProfile.Country = selectedCountry;

        setUpdateProfileError(undefined);

        try {
          await updateBillingProfile(updatedProfile);
          resetAndCloseEditMode();
        } catch (error) {
          // Assume that any error is due to the address being invalid
          setUpdateProfileError(error);
        } finally {
          setIsValidatingAddress(false);
        }
      }
    }
  }, [
    canManageBilling.response,
    isCompleteAddress,
    billingProfile,
    resetAndCloseEditMode,
    selectedCountry,
    updateBillingProfile,
    updatedAddress1,
    updatedAddress2,
    updatedCity,
    updatedState,
    updatedZip,
  ]);

  return (
    <StyledBody>
      {isInEditMode ? (
        <EditContainer>
          <EditTitleContainer>
            <StyledHeader color="SECONDARY_TEXT">Address</StyledHeader>
            <Button
              size="middle"
              shape="round"
              type="secondary"
              dataCy="discard-email-changes"
              styleOverride={CloseCircleButtonStyle}
              onClick={resetAndCloseEditMode}
            >
              <Icon icon={'closeOutlined'} size={16} color="black" />
            </Button>
          </EditTitleContainer>
          <VerticalStack>
            {updateProfileError && (
              <StyledCardStatus
                status="error"
                text={updateProfileError.message}
              />
            )}
            <StyledSelect
              width={'15%'}
              value={selectedCountry || undefined}
              key="countrySelect"
              placeholder="Country"
              disabled={isFetchingProfile}
              options={['CA', 'US'].map(country => ({
                label: country,
                value: country,
              }))}
              onChange={value => setSelectedCountry(value as string)}
              dataCy="country-select"
            />

            <StyledLabel>Address 1</StyledLabel>
            <Input
              onChange={setUpdatedAddress1}
              placeholder="Street Address"
              value={updatedAddress1}
              disabled={isFetchingProfile}
              required
              width="50%"
              dataCy="address-1-input-field"
            />
            <StyledLabel>Address 2</StyledLabel>
            <Input
              onChange={setUpdatedAddress2}
              placeholder="Apt, suite (optional)"
              value={updatedAddress2}
              disabled={isFetchingProfile}
              width="50%"
              dataCy="address-2-input-field"
            />
            <HorizontalStack>
              <VerticalStack>
                <StyledLabel>City</StyledLabel>
                <Input
                  onChange={setUpdatedCity}
                  value={updatedCity}
                  disabled={isFetchingProfile}
                  dataCy="city-input-field"
                />
              </VerticalStack>
              <VerticalStack>
                <StyledLabel>
                  {selectedCountry === 'CA' ? 'Province' : 'State'}
                </StyledLabel>
                <Input
                  onChange={setUpdatedState}
                  value={updatedState}
                  disabled={isFetchingProfile}
                  dataCy="state-input-field"
                />
              </VerticalStack>
            </HorizontalStack>
            <StyledLabel>
              {selectedCountry === 'CA' ? 'Postal Code' : 'Zip Code'}
            </StyledLabel>
            <StyledInput
              onChange={setUpdatedZip}
              value={updatedZip}
              disabled={isFetchingProfile}
              width="20%"
              dataCy="zip-input-field"
            />
          </VerticalStack>
          <Button
            size="middle"
            disabled={
              !isCompleteAddress ||
              isUpdatingProfile ||
              isValidatingAddress ||
              !canManageBilling.response ||
              !hasSavedCard
            }
            dataCy="save-button"
            styleOverride={SaveButtonWidth}
            onClick={updateAddress}
          >
            {
              // Display the text or a loading spinner
              isUpdatingProfile || isValidatingAddress ? (
                <Spin dataCy="address-save-btn-spinner" size="small" />
              ) : (
                'Save'
              )
            }
          </Button>
        </EditContainer>
      ) : (
        <>
          <DetailsContainer>
            <StyledHeader color="SECONDARY_TEXT">Address</StyledHeader>
            {formattedAddress ? (
              <StyledDescription>{formattedAddress}</StyledDescription>
            ) : (
              <StyledDescription>Not provided</StyledDescription>
            )}
          </DetailsContainer>
          <StyledButton
            onClick={switchToEditMode}
            type="secondary"
            minWidth={100}
            disabled={
              !canManageBilling.response || !hasSavedCard || isUpdatingProfile
            }
            dataCy="address-update-btn"
          >
            {'Update'}
          </StyledButton>
        </>
      )}
    </StyledBody>
  );
};

export default BillingAddressCard;

const StyledCardStatus = styled(CardStatus)`
  margin: 8px 0;
`;

const StyledLabel = styled(Text)`
  color: ${Color.SECONDARY_TEXT};
  margin-bottom: 8px;
  margin-top: 16px;
`;

const VerticalStack = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
`;

const HorizontalStack = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: 16px;
  width: 50%;
`;

const StyledSelect = styled(Select)`
  margin-top: 8px;
`;

const StyledButton = styled(Button)`
  &:disabled {
    background-color: ${Color.LIGHTER_GREY};
    color: ${Color.BORDER_GREY};
    border: none;
  }
`;

const StyledBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const EditContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledHeader = styled(Text)`
  margin-bottom: 8px;
`;

const StyledInput = styled(Input)`
  margin-bottom: 8px;
`;

const StyledDescription = styled(Text)`
  color: ${Color.PRIMARY_TEXT};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
`;

const EditTitleContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: top;
`;

const CloseCircleButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  maxWidth: 32,
  minWidth: 32,
  gap: 8,
  padding: 0,
  borderColor: Color.BORDER_GREY,
  marginLeft: 24,
};

const SaveButtonWidth = {
  width: 73,
  height: 40,
  maxWidth: 73,
  minWidth: 73,
};
